// MasterBarangPage.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Colors } from "../../../assets/Colors";
import { useNavigate } from "react-router-dom";
import { fetchGet, fetchPost, fetchPut, fetchDelete } from "../../../fetchers";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card } from "@material-tailwind/react";
import { useForm } from "../../../components/isRequired";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import TableMasterBarang from "../../../components/tables/TableMasterBarang.js";
import debounce from "lodash/debounce";
import { downloadExcel } from "react-export-table-to-excel";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  code39Svg,
  generateBarcodeValue,
  isValidCode39,
  sanitizeCode39,
} from "../../../components/barcode/code39";

// Constants
const INITIAL_STATE = {
  BarangID: "",
  BarangName: "",
  SpesifikasiBarang: "",
  Status: 1,
  KelompokID: "",
  DetailAccountID: "",
  LawanAccountID: "",
  LawanBebanAccountID: "",
  SatuanID: "",
  Tipe: "",
  PartNumber: "",
  MinQty: 0,
  KodeJenisBarang: "",
  JenisBarang: "",
  NamaBagianKodeJenis: "",
  Barcode: "",
};

const VALIDATIONS = [
  ({ BarangName }) => !BarangName && { BarangName: "Nama Barang wajib diisi" },
  ({ Status }) => Status == null && { Status: "Status wajib diisi" },
  ({ SpesifikasiBarang }) =>
    !SpesifikasiBarang && {
      SpesifikasiBarang: "Spesifikasi Barang wajib diisi",
    },
  ({ KelompokID }) => !KelompokID && { KelompokID: "Kelompok wajib diisi" },
  ({ DetailAccountID }) =>
    !DetailAccountID && { DetailAccountID: "Detail Account wajib diisi" },
  ({ SatuanID }) => !SatuanID && { SatuanID: "Satuan wajib diisi" },
  // Backend menolak (400) bila Tipe kosong, jadi tangkap di form.
  ({ Tipe }) => !Tipe && { Tipe: "Tipe wajib diisi" },
];

const INPUT_BUILDER = [
  {
    type: "text",
    name: "BarangID",
    title: "Barang ID",
    disabled: true,
    edit: true,
  },
  { type: "BagianKodeJenis", name: "KodeJenisBarang", title: "Kode Jenis Barang", handler: "bagianKodeJenis" },
  {
    type: "JenisBarang",
    name: "JenisBarang",
    title: "Jenis Barang",
    handler: "custom",
  },
  {
    type: "Kelompok",
    name: "KelompokID",
    title: "Kelompok",
    handler: "custom",
  },
  {
    type: "DetailAccount",
    name: "DetailAccountID",
    title: "Detail Account",
    handler: "custom",
    disabled: true,
  },
  {
    type: "LawanDetailAccount",
    name: "LawanAccountID",
    title: "COA Hutang",
    handler: "custom",
    disabled: true,
  },
  {
    type: "LawanDetailAccountBeban",
    name: "LawanBebanAccountID",
    title: "COA Beban",
    handler: "custom",
    disabled: true,
  },
  { type: "TipePR", name: "Tipe", title: "Tipe", handler: "custom" },
  { type: "text", name: "BarangName", title: "Barang Name" },
  { type: "text", name: "SpesifikasiBarang", title: "Spesifikasi Barang" },
  { type: "text", name: "PartNumber", title: "Part Number" },
  { type: "number", name: "MinQty", title: "Minimum Stock" },
  { type: "Satuan", name: "SatuanID", title: "Satuan", handler: "custom" },
];

// Opsi untuk filter Jenis Barang
const JENIS_BARANG_OPTIONS = [
  { value: "", label: "Semua Jenis Barang" },
  { value: "Logistik", label: "Logistik" },
  { value: "Material", label: "Material" },
];

const escapeHtml = (s) =>
  (s ?? "")
    .toString()
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Membuka jendela cetak berisi `qty` label barcode untuk satu barang.
// Label memakai satuan mm supaya ukurannya konsisten di kertas, bukan di layar.
const openBarcodePrintWindow = (item, qty) => {
  // Parameter dipilih untuk label kecil 30x15mm: barcode digambar dengan aspek
  // yang cukup tinggi (height besar) agar batangnya TINGGI saat diperkecil ke
  // lebar ~28mm — bersama teks nama yang dikecilkan, barcode jadi lebih besar
  // & lebih mudah di-scan.
  const svg = code39Svg(item.Barcode, {
    narrow: 1.6,
    ratio: 3,
    height: 105,
    fontSize: 9,
  });
  if (!svg) return false;

  // Label 30x15mm terlalu kecil untuk 3 baris. Ditampilkan: nama (1 baris,
  // dipangkas) + barcode (angka kodenya sudah tercetak di dalam SVG). BarangID
  // digabung ke baris nama agar tetap terlihat bila muat.
  const namaLabel = item.SatuanName
    ? `${item.BarangName} (${item.SatuanName})`
    : item.BarangName;
  const label = `<div class="label">
      <div class="nama">${escapeHtml(namaLabel)}</div>
      <div class="bar">${svg}</div>
    </div>`;

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return false;

  win.document.write(`<!doctype html>
<html lang="id">
<head>
<meta charset="utf-8" />
<title>Barcode ${escapeHtml(item.BarangID)}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; padding: 8mm; font-family: Arial, Helvetica, sans-serif; }
  /* Kertas label khusus 30 x 15 mm: SATU label = SATU halaman kertas label.
     Saat mencetak, ukuran kertas printer HARUS diset 30x15mm (media label).
     Di layar, label ditumpuk sbg pratinjau; di kertas, tiap label satu halaman. */
  .wrap { font-size: 0; }
  .label {
    display: flex; flex-direction: column; text-align: center;
    width: 30mm; height: 15mm; padding: 1mm; margin: 0 0 2mm 0;
    border: 1px dashed #bbb; overflow: hidden;
    page-break-inside: avoid; break-inside: avoid;
  }
  .nama {
    font-size: 4pt; font-weight: 700; line-height: 1;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-bottom: 0.2mm; flex: none;
  }
  /* Barcode mengisi sisa tinggi; SVG dibatasi agar tak pernah melampaui kotak. */
  .bar {
    flex: 1; min-height: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .bar svg { max-width: 100%; max-height: 100%; height: auto; }
  .toolbar { margin-bottom: 6mm; }
  .toolbar button {
    padding: 8px 16px; font-size: 13px; cursor: pointer;
    border: 1px solid #888; background: #fff; border-radius: 4px;
  }
  @media print {
    body { padding: 0; }
    /* Ukuran halaman = ukuran label. Tiap label mendapat halamannya sendiri. */
    @page { size: 30mm 15mm; margin: 0; }
    .toolbar { display: none; }
    .label {
      width: 30mm; height: 15mm; margin: 0; border: 0;
      page-break-after: always; break-after: page;
    }
  }
</style>
</head>
<body>
  <div class="toolbar">
    <button onclick="window.print()">Cetak</button>
    <span style="margin-left:8px;font-size:12px;color:#555">
      ${qty} label &middot; ${escapeHtml(item.BarangName)}
    </span>
  </div>
  <div class="wrap">${label.repeat(qty)}</div>
</body>
</html>`);
  win.document.close();
  win.focus();
  return true;
};

const MasterBarangPage = () => {
  const navigate = useNavigate();

  // State
  const [buttonTouched, setButtonTouched] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [search, setSearch] = useState("");
  const [jenisbarang, setJenisbarang] = useState(""); // Empty string = All/Semua data
  const [bagian, setBagian] = useState(""); // BagianID; "" = semua bagian
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(maxPageGlobal);
  const [isExporting, setIsExporting] = useState(false);
  // Barang yang sedang disiapkan cetak barcode-nya (null = modal tertutup).
  const [printItem, setPrintItem] = useState(null);
  const [printQty, setPrintQty] = useState(1);

  // Form handling
  const form = useForm(INITIAL_STATE, VALIDATIONS);
  const {
    values,
    isValid,
    touched,
    clearHandler,
    customHandler,
    changeHandler,
    multiFieldHandler,
    errors,
    editHandler,
  } = form;

  const bagianKodeJenisHandler = useCallback((value, name, detailAccountID, label, lawanAccountID, lawanBebanAccountID) => {
    multiFieldHandler({
      [name]: value,
      DetailAccountID: detailAccountID || "",
      LawanAccountID: lawanAccountID || "",
      LawanBebanAccountID: lawanBebanAccountID || "",
      NamaBagianKodeJenis: label || value,
    });
  }, [multiFieldHandler]);

  // Debounced search
  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setPage(1);
      }, 500),
    [],
  );

  // Cleanup debounce
  useEffect(() => {
    return () => debouncedSetSearch.cancel();
  }, [debouncedSetSearch]);

  // Opsi filter Bagian. Sumbernya master Bagian yang sama dengan yang dipakai
  // field "Kode Jenis Barang" (kolom KodeJenisBarang memang berisi BagianID).
  const { data: bagianData } = useQuery({
    queryKey: ["masterBagianMin"],
    queryFn: () => fetchGet("getMinMasterBagianV2"),
    staleTime: 5 * 60 * 1000,
  });

  const bagianOptions = useMemo(
    () => [{ value: "", label: "Semua Bagian" }, ...(bagianData?.data ?? [])],
    [bagianData],
  );

  // Fetch data for display (pagination)
  const {
    data: barangData,
    isLoading: isLoadingData,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["masterBarang", search, jenisbarang, bagian, page, limit],
    queryFn: () => {
      return fetchGet("Master/Barang", {
        search: search || undefined,
        ...(jenisbarang && { jenisbarang }),
        ...(bagian && { bagian }),
        page,
        limit,
      });
    },
    keepPreviousData: true,
    staleTime: 30000,
  });

  const exportHeaders = [
    "No", "Barang ID", "Bagian", "Barcode", "Jenis Barang", "Kelompok",
    "Detail Account", "Tipe", "Nama Barang", "Spesifikasi",
    "Part Number", "Satuan", "Min Stock", "Status", "Kode Stock Opname",
  ];

  const fetchAllDataForExport = useCallback(async () => {
    const res = await fetchGet("Master/Barang", {
      search: search || undefined,
      ...(jenisbarang && { jenisbarang }),
      ...(bagian && { bagian }),
      page: 1,
      limit: 999999,
    });
    return res.data || [];
  }, [search, jenisbarang, bagian]);

  const buildExportRows = (data) =>
    data.map((item, i) => [
      i + 1,
      item.BarangID || "",
      item.NamaBagian || item.KodeJenisBarang || "",
      item.Barcode || "",
      item.JenisBarang || "",
      item.KelompokName || "",
      item.NamaDetailAccount || "",
      item.Tipe || "",
      item.BarangName || "",
      item.SpesifikasiBarang || "",
      item.PartNumber || "",
      item.SatuanName || "",
      item.MinQty || 0,
      item.Status === 1 ? "Aktif" : "Non-Aktif",
      item.KodeStockOpname || "",
    ]);

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const data = await fetchAllDataForExport();
      if (!data.length) { toast.warn("Tidak ada data untuk di-export", { position: "top-center" }); return; }
      downloadExcel({
        fileName: "Master_Barang",
        sheet: "Barang",
        tablePayload: { header: exportHeaders, body: buildExportRows(data) },
      });
    } catch (e) {
      toast.error("Gagal export: " + (e.message || ""), { position: "top-center" });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport]);

  const handleExportPDF = useCallback(async () => {
    setIsExporting(true);
    try {
      const data = await fetchAllDataForExport();
      if (!data.length) { toast.warn("Tidak ada data untuk di-export", { position: "top-center" }); return; }
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("Master Barang", 14, 15);
      autoTable(doc, {
        startY: 22,
        head: [exportHeaders],
        body: buildExportRows(data),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [41, 128, 185] },
      });
      doc.save("Master_Barang.pdf");
    } catch (e) {
      toast.error("Gagal export: " + (e.message || ""), { position: "top-center" });
    } finally {
      setIsExporting(false);
    }
  }, [fetchAllDataForExport]);

  // Mutation handlers
  const handleMutationSuccess = useCallback(
    (action) => {
      setShowModal(false);
      setShowModalEdit(false);
      toast.success(`Data berhasil ${action}!`, {
        position: "top-center",
        autoClose: 1000,
      });
      refetch();
      clearHandler();
    },
    [refetch, clearHandler],
  );

  const handleMutationError = useCallback((action, error) => {
    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      `Terjadi kesalahan saat ${action} data`;

    toast.error(`Gagal ${action} data: ${errorMessage}`, {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: true,
    });
  }, []);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (formData) => fetchPost("Master/Barang", formData),
    onSuccess: () => handleMutationSuccess("ditambahkan"),
    onError: (error) => handleMutationError("menambahkan", error),
  });

  const statusMutation = useMutation({
    mutationFn: (formData) => fetchPost("Master/Barang/Action", formData),
    onSuccess: () => {
      toast.success("Status berhasil diubah!", {
        position: "top-center",
        autoClose: 1000,
      });
      refetch();
    },
    onError: (error) => handleMutationError("mengubah status", error),
  });

  const deleteMutation = useMutation({
    mutationFn: (formData) => fetchDelete("Master/Barang", formData),
    onSuccess: () => {
      handleMutationSuccess("dihapus");
    },
    onError: (error) => handleMutationError("menghapus", error),
  });

  const updateMutation = useMutation({
    mutationFn: (formData) => fetchPut("Master/Barang", formData),
    onSuccess: () => handleMutationSuccess("diupdate"),
    onError: (error) => handleMutationError("mengupdate", error),
  });

  // Event handlers
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  const handleLimitChange = useCallback((newLimit) => {
    setLimit(newLimit === -1 ? 999999 : newLimit);
    setPage(1);
  }, []);

  const handleSearch = useCallback(
    (searchValue) => {
      debouncedSetSearch(searchValue);
    },
    [debouncedSetSearch],
  );

  const handleRefresh = useCallback(() => {
    refetch();
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [refetch]);

  const handleAdd = useCallback(() => {
    setShowModal(true);
    setShowModalEdit(false);
    clearHandler();
  }, [clearHandler]);

  const handleEdit = useCallback(
    (item) => {
      setShowModal(true);
      setShowModalEdit(true);
      editHandler(item);
    },
    [editHandler],
  );

  const handleAction = useCallback(
    (item) => {
      statusMutation.mutate({ BarangID: item.BarangID });
    },
    [statusMutation],
  );

  const handleDelete = useCallback(
    (item) => {
      const confirmDelete = window.confirm(
        `Apakah Anda yakin ingin menghapus barang "${item.BarangName}"?\n` +
          `ID: ${item.BarangID}`,
      );

      if (confirmDelete) {
        deleteMutation.mutate({ BarangID: item.BarangID });
      }
    },
    [deleteMutation],
  );

  const handleSubmit = useCallback(() => {
    setButtonTouched(true);
    if (!isValid) {
      toast.warn("Mohon lengkapi semua kolom yang wajib diisi.", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    // Backend mewajibkan PartNumber; barang tanpa nomor part diisi "-".
    const payload = {
      ...values,
      PartNumber: (values.PartNumber || "").trim() || "-",
      MinQty: parseInt(values.MinQty, 10) || 0,
    };
    const mutationFn = showModalEdit ? updateMutation : createMutation;
    mutationFn.mutate(payload);
  }, [isValid, showModalEdit, values, updateMutation, createMutation]);

  // Filter handler untuk Jenis Barang dengan select biasa
  const handleJenisBarangChange = useCallback((e) => {
    const value = e.target.value;
    setJenisbarang(value);
    setPage(1);
  }, []);

  const handleBagianChange = useCallback((e) => {
    setBagian(e.target.value);
    setPage(1);
  }, []);

  // Isi kolom Barcode dengan kode baru. Tidak menimpa diam-diam: kalau sudah
  // terisi, minta konfirmasi dulu supaya label yang sudah tercetak & tertempel
  // tidak jadi yatim tanpa disadari.
  const handleGenerateBarcode = useCallback(() => {
    if (values.Barcode) {
      const ok = window.confirm(
        "Barcode saat ini akan diganti dengan kode baru.\n" +
          "Label yang sudah tercetak & tertempel tidak akan cocok lagi.\n\nLanjutkan?",
      );
      if (!ok) return;
    }
    customHandler(generateBarcodeValue(), "Barcode");
  }, [values.Barcode, customHandler]);

  const handleBarcodeChange = useCallback(
    (e) => {
      // Code 39 hanya mengenal 0-9, A-Z, dan - . spasi $ / + % — karakter lain
      // dibuang saat diketik supaya tak ada barcode yang tersimpan tapi tak
      // bisa digambar/di-scan.
      customHandler(sanitizeCode39(e.target.value), "Barcode");
    },
    [customHandler],
  );

  const handlePrintBarcode = useCallback((item) => {
    if (!item?.Barcode) {
      toast.warn(
        `"${item?.BarangName || "Barang ini"}" belum punya barcode. ` +
          "Isi lewat Edit (ada tombol Generate) lalu simpan dulu.",
        { position: "top-center", autoClose: 4000 },
      );
      return;
    }
    setPrintItem(item);
    setPrintQty(1);
  }, []);

  const handleConfirmPrint = useCallback(() => {
    const qty = parseInt(printQty, 10);
    if (!qty || qty < 1) {
      toast.warn("Jumlah cetak minimal 1.", { position: "top-center" });
      return;
    }
    if (qty > 500) {
      toast.warn("Maksimal 500 label sekali cetak.", { position: "top-center" });
      return;
    }
    const opened = openBarcodePrintWindow(printItem, qty);
    if (!opened) {
      toast.error(
        "Jendela cetak diblokir browser. Izinkan popup untuk situs ini lalu ulangi.",
        { position: "top-center", autoClose: 5000 },
      );
      return;
    }
    setPrintItem(null);
  }, [printItem, printQty]);

  // Data preparation
  const tableData = barangData?.data || [];
  const totalRows = barangData?.count || 0;
  const isLoading = isLoadingData || isFetching || deleteMutation.isLoading;


  return (
    <div className="md:ml-64">
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card>
              <div className="p-6">
                {/* Back Button */}
                <div className="mb-4">
                  <button
                    onClick={() => navigate("/master")}
                    className="flex items-center gap-1 bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm"
                  >
                    &#8592; Kembali
                  </button>
                </div>

                {/* Filter Section */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-4 items-end">
                    {/* Filter Jenis Barang */}
                    <div>
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Jenis Barang
                      </label>
                      <select
                        value={jenisbarang}
                        onChange={handleJenisBarangChange}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {JENIS_BARANG_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Filter Bagian — nilainya BagianID, dicocokkan backend
                        ke kolom KodeJenisBarang. */}
                    <div>
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Bagian
                      </label>
                      <select
                        value={bagian}
                        onChange={handleBagianChange}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent max-w-xs"
                      >
                        {bagianOptions.map((option) => (
                          <option key={option.value || "all"} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Export Buttons */}
                    <div>
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        &nbsp;
                      </label>
                      <div className="flex flex-row gap-2">
                        <button
                          onClick={handleExportExcel}
                          disabled={isExporting || isLoading}
                          className="flex flex-row items-center justify-center bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm disabled:opacity-50"
                        >
                          {isExporting ? "Mengexport..." : "Download Excel"}
                        </button>
                        <button
                          onClick={handleExportPDF}
                          disabled={isExporting || isLoading}
                          className="flex flex-row items-center justify-center bg-white hover:bg-gray-100 text-gray-800 font-semibold py-2 px-4 border border-gray-400 rounded shadow text-sm disabled:opacity-50"
                        >
                          {isExporting ? "Mengexport..." : "Download PDF"}
                        </button>
                      </div>
                    </div>

                    {/* Info Total Data */}
                    <div className="text-sm text-gray-600 self-end pb-2">
                      Total: {totalRows} data
                      {jenisbarang && ` (Jenis: ${jenisbarang})`}
                    </div>
                  </div>
                </div>

                {/* Table Component */}
                <TableMasterBarang
                  data={tableData}
                  onEdit={handleEdit}
                  onAction={handleAction}
                  onDelete={handleDelete}
                  onPrintBarcode={handlePrintBarcode}
                  onAdd={handleAdd}
                  isLoading={isLoading}
                  onPageChange={handlePageChange}
                  onPerRowsChange={handleLimitChange}
                  totalRows={totalRows}
                  currentPage={page}
                  perPage={limit}
                  search={search}
                  onSearchChange={handleSearch}
                  onRefresh={handleRefresh}
                  jenisBarang={jenisbarang}
                  onJenisBarangChange={handleJenisBarangChange}
                />
              </div>
            </Card>

            {/* Modal */}
            <Modal
              size="lg"
              active={showModal}
              toggler={() => {
                setShowModal(false);
                setShowModalEdit(false);
                clearHandler();
              }}
            >
              <ModalHeader
                toggler={() => {
                  setShowModal(false);
                  setShowModalEdit(false);
                  clearHandler();
                }}
              >
                {showModalEdit ? "Edit Data Barang" : "Tambah Data Barang"}
              </ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  {INPUT_BUILDER.map((input, index) => {
                    if (!input.edit || showModalEdit) {
                      return (
                        <div key={`${input.name}-${index}`} className="mb-4">
                          <HrdInputGenerator
                            type={input.type}
                            value={values}
                            name={input.name}
                            title={input.title}
                            onChange={
                              input.handler === "custom"
                                ? customHandler
                                : input.handler === "bagianKodeJenis"
                                ? bagianKodeJenisHandler
                                : changeHandler
                            }
                            touched={touched}
                            buttonTouched={buttonTouched}
                            size={input.size || "full"}
                            required={input.required}
                            errors={errors}
                            disabled={input.disabled}
                          />
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Barcode — bukan bagian INPUT_BUILDER karena butuh tombol
                      Generate & pratinjau di sebelahnya. Boleh dikosongkan. */}
                  <div className="mb-4">
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Barcode <span className="normal-case font-normal text-gray-400">(opsional)</span>
                    </label>
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
                      <input
                        type="text"
                        value={values.Barcode || ""}
                        onChange={handleBarcodeChange}
                        placeholder="Kosongkan bila barang ini tidak memakai barcode"
                        className="appearance-none block w-full text-gray-700 border border-gray-500 rounded-lg py-3 px-4 leading-tight outline-none bg-white font-mono"
                      />
                      <button
                        type="button"
                        onClick={handleGenerateBarcode}
                        className="whitespace-nowrap px-4 py-3 bg-white hover:bg-gray-100 text-gray-800 font-semibold border border-gray-400 rounded-lg shadow text-sm"
                      >
                        Generate
                      </button>
                    </div>
                    {values.Barcode ? (
                      isValidCode39(values.Barcode) ? (
                        <div className="mt-3 flex flex-col items-start gap-1">
                          <div
                            className="border border-gray-200 rounded p-2 bg-white"
                            dangerouslySetInnerHTML={{
                              __html: code39Svg(values.Barcode, {
                                narrow: 1.6,
                                ratio: 3,
                                height: 45,
                                fontSize: 11,
                              }),
                            }}
                          />
                          <span className="text-xs text-gray-500">
                            Pratinjau Code 39. Tersimpan setelah menekan Simpan.
                          </span>
                        </div>
                      ) : (
                        <p className="mt-2 text-xs text-red-500">
                          Barcode mengandung karakter yang tidak didukung Code 39.
                        </p>
                      )
                    ) : null}
                  </div>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="red"
                  buttonType="link"
                  onClick={() => {
                    setShowModal(false);
                    setShowModalEdit(false);
                    clearHandler();
                  }}
                  ripple="dark"
                >
                  Batal
                </Button>

                <Button
                  color={isValid ? "green" : "blueGray"}
                  disabled={
                    createMutation.isLoading ||
                    updateMutation.isLoading ||
                    !isValid
                  }
                  onClick={handleSubmit}
                  ripple={isValid ? "light" : ""}
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? "Memuat..."
                    : "Simpan"}
                </Button>
              </ModalFooter>
            </Modal>

            {/* Modal cetak barcode — jumlah label ditentukan pengguna.
                size="lg": barcode 12 digit tidak muat di modal kecil. */}
            <Modal
              size="lg"
              active={!!printItem}
              toggler={() => setPrintItem(null)}
            >
              <ModalHeader toggler={() => setPrintItem(null)}>
                Cetak Barcode
              </ModalHeader>
              <ModalBody>
                {printItem && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">
                        {printItem.BarangName}
                      </div>
                      <div className="text-xs text-gray-500 font-mono">
                        {printItem.BarangID} &middot; {printItem.Barcode}
                      </div>
                    </div>

                    <div className="w-full overflow-x-auto rounded border border-gray-200 bg-white p-4">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: code39Svg(printItem.Barcode, {
                            narrow: 2.2,
                            ratio: 3,
                            height: 70,
                            fontSize: 14,
                          }),
                        }}
                      />
                    </div>

                    <div>
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Jumlah label
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={500}
                        value={printQty}
                        onChange={(e) => setPrintQty(e.target.value)}
                        className="appearance-none block w-32 text-gray-700 border border-gray-500 rounded-lg py-2 px-3 leading-tight outline-none bg-white"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Maksimal 500 label sekali cetak. Label terbuka di jendela
                        baru — pastikan popup tidak diblokir.
                      </p>
                      <p className="mt-1 text-xs text-gray-500">
                        Ukuran label <b>30&times;15&nbsp;mm</b> — satu label per
                        halaman. Di dialog cetak set <b>ukuran kertas 30&times;15&nbsp;mm</b>
                        (media label), <b>Margin: None</b>, dan <b>Skala 100%</b>.
                      </p>
                    </div>
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button
                  color="red"
                  buttonType="link"
                  onClick={() => setPrintItem(null)}
                  ripple="dark"
                >
                  Batal
                </Button>
                <Button color="green" onClick={handleConfirmPrint} ripple="light">
                  Cetak
                </Button>
              </ModalFooter>
            </Modal>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MasterBarangPage;